// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

// COB-500 placement: the seam between the control (Wallet page) and the
// flow (mounted in `Layout`).
//
// WHY the two are apart. The wipe's last step is `session.logout()`, which
// flips the session to `unauthenticated` — and `pages/wallet.tsx` renders
// `null` the moment `isLoggedIn` goes false (and routes to `/`). A modal
// mounted inside the Wallet page would therefore be torn down at the exact
// instant it has something to say: the confirmation panel reporting what was
// deleted would never render, and the user would be dumped on the sign-in
// page with no evidence the wipe ran. So the modal stays mounted in `Layout`
// — outside every auth gate — and the Wallet card reaches it through here.
//
// WHY a module-level trigger rather than a window CustomEvent (the idiom the
// agent lock/unlock signals use): this opens a DESTRUCTIVE flow, and a
// stringly-typed global channel can be fired by anything on the page. The
// only way to open this one is to import `openBrowserWipe`, which keeps the
// call sites greppable and the surface exactly one function wide.

type OpenListener = () => void;

/** The single mounted flow. `Layout` is a singleton in the tree (app.tsx
 *  wraps the whole Router in one), so this is deliberately one slot and not
 *  a subscriber list — two registrations would mean two modals. */
let mounted: OpenListener | null = null;

/**
 * Register the mount point that owns the flow. Called by `Layout` in an
 * effect; the returned function unregisters, and only if this registration
 * is still the live one (a remount registers before the old cleanup runs).
 */
export function registerBrowserWipeMount(open: OpenListener): () => void {
  mounted = open;
  return () => {
    if (mounted === open) mounted = null;
  };
}

/**
 * Open the wipe flow. Returns whether a mount point took it — false only
 * when the caller is rendered outside a `Layout`, which in the app cannot
 * happen (every route is inside one) but is worth reporting rather than
 * swallowing in a test harness.
 */
export function openBrowserWipe(): boolean {
  if (!mounted) return false;
  mounted();
  return true;
}
